/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "dashboard-template/examples/Sidenav";
import Configurator from "dashboard-template/examples/Configurator";

// Material Dashboard 2 PRO React TS themes
import theme from "dashboard-template/assets/theme";
import themeRTL from "dashboard-template/assets/theme/theme-rtl";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "dashboard-template/assets/theme-dark";
import themeDarkRTL from "dashboard-template/assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React TS routes
import paths, { CustomRouteType } from "routes";
import navLinks from "nav.routes";

// Global Components
import GlobalVideochat from "components/global/GlobalVideochat";
import LoadingOverlay from "components/generals/LoadingOverlay";

import "i18n.ts";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator, setSidenavColor, setTransparentSidenav, setWhiteSidenav, setDarkMode, setFixedNavbar, setLanguage } from "context/ThemeContext";

// Images
import brand from "assets/logos/free-healthcare-icon-3610-thumb.png";

import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { CoreAPI_URLs } from "constants/http.const";
import { selectCurrentUser } from "store/slices/auth.slice";

/**
 * Main Application component for Medical Record System.
 * 
 * Responsibilities:
 * - Theme management (light/dark mode, RTL support)
 * - Sidebar navigation with permission-based filtering
 * - Route rendering with authentication checks
 * - Global videochat integration
 * - Responsive layout configuration
 * 
 * Features:
 * - Material UI theming with RTL (Right-to-Left) support
 * - Permission-based navigation filtering from Redux auth state
 * - Mini/expanded sidebar with mouse hover behavior
 * - Theme configurator panel
 * - Public route detection (hides sidebar for guest videochat)
 * 
 * The component uses Material Dashboard 2 PRO React template for UI consistency
 * and integrates with Redux for state management across the application.
 * 
 * @returns Main application component with routing and layout
 */
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const userPermissions = useSelector((state: RootState) => state.auth.permissions);
  const currentUser = useSelector(selectCurrentUser);
  const isGlobalLoading = useSelector((state: RootState) => state.event.isGlobalLoading);
  
  // Check if current route is a public/guest route that shouldn't show sidebar
  const isPublicRoute = pathname.startsWith(CoreAPI_URLs.VIDEOCHAT_GUEST);
  
  // Check if current route is the full-screen asset viewer
  const isFullScreenRoute = pathname.startsWith(CoreAPI_URLs.ASSET_VIEWER);

  const [filteredNavLinks, setFilteredNavLinks] = useState([]);

  useEffect(() => {
    if (currentUser?.profile) {
      const profile = currentUser.profile;
      
      if (profile.sidenavColor) {
        setSidenavColor(dispatch, profile.sidenavColor);
      }
      
      if (profile.transparentSidenav !== undefined) {
        setTransparentSidenav(dispatch, profile.transparentSidenav);
      }
      
      if (profile.whiteSidenav !== undefined) {
        setWhiteSidenav(dispatch, profile.whiteSidenav);
      }
      
      if (profile.darkMode !== undefined) {
        setDarkMode(dispatch, profile.darkMode);
      }
      
      if (profile.miniSidenav !== undefined) {
        setMiniSidenav(dispatch, profile.miniSidenav);
      }
      
      if (profile.fixedNavbar !== undefined) {
        setFixedNavbar(dispatch, profile.fixedNavbar);
      }
      
      if (profile.language) {
        setLanguage(dispatch, profile.language as "en" | "es" | "zh" | "fr" | "it" | "pt");
      }
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (!userPermissions || userPermissions.length === 0) {
      setFilteredNavLinks([]);
      return;
    }

    const newFilteredNavLinks = navLinks
      .map((route) => {
        if (route.collapse) {
          const filteredCollapse = route.collapse.filter(
            (child) =>
              !child.requiredPermission || userPermissions.includes(child.requiredPermission)
          );
          if (filteredCollapse.length > 0) {
            return { ...route, collapse: filteredCollapse };
          }
          return null;
        }

        if (route.requiredPermission) {
          return userPermissions.includes(route.requiredPermission) ? route : null;
        }

        return route;
      })
      .filter(Boolean);

    setFilteredNavLinks(newFilteredNavLinks);
  }, [userPermissions, navLinks]);

  // Cache for the rtl
  useMemo(() => {
    const pluginRtl: any = rtlPlugin;
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [pluginRtl],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes: CustomRouteType[]): any =>
    allRoutes.map((route: CustomRouteType) => {
      return (
        <Route
          path={route.path}
          element={route.redirect ? <Navigate to={route.redirect} replace /> : route.element}
          key={route.id}
        />
      );
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {isGlobalLoading && <LoadingOverlay />}
        {layout === "dashboard" && !isPublicRoute && !isFullScreenRoute && (
          <>
            {!userPermissions || userPermissions.length === 0 ? (
              <MDBox>Loading...</MDBox>
            ) : (
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || brand}
                brandName="Medical Record System"
                routes={filteredNavLinks}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            )}
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(paths)}
          <Route path="*" element={<Navigate to="/dashboards/404page" />} />
        </Routes>
        <GlobalVideochat />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {isGlobalLoading && <LoadingOverlay />}
      {layout === "dashboard" && !isPublicRoute && !isFullScreenRoute && (
        <>
          {!userPermissions || userPermissions.length === 0 ? (
            <MDBox>Loading...</MDBox>
          ) : (
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || brand}
              brandName="Medical Record System"
              routes={filteredNavLinks}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(paths)}
        <Route path="*" element={<Navigate to="/dashboards/404page" />} />
      </Routes>
      <GlobalVideochat />
    </ThemeProvider>
  );
}

